import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, useMotionValue, useTransform, animate, useReducedMotion } from 'framer-motion';
import { useDrag } from '@use-gesture/react';
import { Bell, ChevronRight, Loader2, Trash2 } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Card } from '@shared/ui/containers/Card';
import { ConfirmSheet } from '@shared/ui/containers/ConfirmSheet';
import { ListGroup } from '@shared/ui/lists/ListGroup';
import { Button } from '@shared/ui/primitives/Button';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Banner } from '@shared/ui/feedback/Banner';
import { cn } from '@shared/lib/cn';
import { formatRelative } from '@shared/lib/format';
import { toMessageSnippet } from '@shared/lib/messageHtml';
import { haptics } from '@shared/lib/haptics';
import { useAuthStore } from '@shared/stores/authStore';
import {
  clearAllNotifications,
  deleteNotification,
  fetchNotifications,
  markNotificationRead,
  type Notification,
} from './api';
import { AttachmentList, readAttachments } from '../communication/AttachmentList';
import { toRenderableMessageHtml } from '../communication/messageHtml';
import { MessageBody } from '../communication/MessageBody';
import { getNotificationTarget } from './notificationTargets';
import { routeNotification } from './routeNotification';
import { useTenantSwitchPrompt } from './tenantSwitchPromptStore';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';

interface NotificationsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationsSheet({ open, onOpenChange }: NotificationsSheetProps): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const listQuery = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => fetchNotifications({ limit: 30 }),
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: clearAllNotifications,
    onSuccess: () => {
      setConfirmClearOpen(false);
      // Reflect the server-side clear immediately (empty list + zero badge),
      // then invalidate so nothing stale survives in the cache.
      queryClient.setQueryData<Notification[]>(['notifications', 'list'], []);
      queryClient.setQueryData<number>(['notifications', 'unread-count'], 0);
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNotification,
    // Optimistically drop the row (and decrement the unread badge) before the
    // request resolves, then roll back if the delete fails.
    onMutate: async (notificationId: string) => {
      await queryClient.cancelQueries({ queryKey: ['notifications', 'list'] });
      const previousList = queryClient.getQueryData<Notification[]>(['notifications', 'list']);
      const removed = previousList?.find((n) => n.notification_id === notificationId);

      queryClient.setQueryData<Notification[]>(['notifications', 'list'], (old) =>
        (old ?? []).filter((n) => n.notification_id !== notificationId),
      );
      if (removed && !removed.is_read) {
        queryClient.setQueryData<number>(['notifications', 'unread-count'], (count) =>
          Math.max(0, (count ?? 0) - 1),
        );
      }

      return { previousList };
    },
    onError: (_err, _notificationId, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(['notifications', 'list'], context.previousList);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const items = listQuery.data ?? [];

  // The open notification lives in the host screen's URL, so back from an attachment's viewer
  // reopens it (the bell opens the sheet when the URL names one).
  const selectedNotification = items.find((n) => n.notification_id === params.get('notification')) ?? null;
  const setSelectedNotification = (notification: Notification | null): void => {
    setParams(
      (next) => {
        if (notification) next.set('notification', notification.notification_id);
        else next.delete('notification');
        return next;
      },
      { replace: true },
    );
  };

  const unreadInList = items.filter((n) => !n.is_read).length;

  const subtitle =
    unreadInList > 0
      ? `${unreadInList} unread notification${unreadInList === 1 ? '' : 's'}`
      : "You're all caught up";

  return (
    <BottomSheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setSelectedNotification(null);
        onOpenChange(nextOpen);
      }}
      title="Notifications"
      description={subtitle}
      height="min(72dvh, 560px)"
      titleAccessory={
        items.length > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            // Stop the tap bubbling to the header's drag handler (would swallow the click).
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => setConfirmClearOpen(true)}
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
            Clear all
          </Button>
        ) : undefined
      }
    >
      {listQuery.isLoading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-body text-ink-secondary">
          <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.75} />
          Loading…
        </div>
      ) : null}

      {listQuery.isError ? (
        <Banner variant="danger" className="mb-4">
          Couldn't load notifications. Pull down to try again.
        </Banner>
      ) : null}

      {!listQuery.isLoading && !listQuery.isError && items.length === 0 ? (
        <EmptyState
          icon={<Bell className="h-10 w-10" strokeWidth={1.25} />}
          title="No notifications"
          description="When something needs your attention, it will show up here."
        />
      ) : null}

      {!listQuery.isLoading && items.length > 0 ? (
        <ListGroup className="pb-2">
          {items.map((item) => (
            <NotificationRow
              key={item.notification_id}
              notification={item}
              onOpen={() => {
                if (!item.is_read) {
                  markReadMutation.mutate(item.notification_id);
                }
                setSelectedNotification(item);
              }}
              onDelete={() => deleteMutation.mutate(item.notification_id)}
            />
          ))}
        </ListGroup>
      ) : null}

      <BottomSheet
        open={selectedNotification != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setSelectedNotification(null);
          }
        }}
        title={selectedNotification?.title}
        description={selectedNotification ? formatRelative(selectedNotification.created_at) : undefined}
        height="min(70dvh, 520px)"
      >
        {selectedNotification ? (
          <NotificationDetail
            notification={selectedNotification}
            onOpenTarget={() => {
              const notification = selectedNotification;
              const auth = useAuthStore.getState();
              routeNotification(notification, {
                activeTenantId: auth.tenantId,
                linkedTenants: auth.linkedTenants,
                navigate: (path) => {
                  setSelectedNotification(null);
                  onOpenChange(false);
                  navigate(path);
                },
                requestSwitch: (req) => {
                  setSelectedNotification(null);
                  onOpenChange(false);
                  useTenantSwitchPrompt.getState().request(req);
                },
              });
            }}
          />
        ) : null}
      </BottomSheet>

      <ConfirmSheet
        open={confirmClearOpen}
        onOpenChange={setConfirmClearOpen}
        title="Clear all notifications?"
        description="This permanently removes all your notifications. This cannot be undone."
        confirmLabel="Clear all"
        loading={clearAllMutation.isPending}
        onConfirm={() => clearAllMutation.mutate()}
      />
    </BottomSheet>
  );
}

/**
 * A notification row with iOS-style swipe-to-dismiss.
 *
 * Dragging the card right-to-left reveals a red "Delete" affordance beneath it;
 * releasing past ~40% of the card width (or a quick left flick) animates the
 * card off-screen and fires `onDelete`. Under threshold it springs back.
 * Rightward drag is clamped to rest. A plain tap still opens the notification
 * (swipes are filtered out via `filterTaps` + a moved-guard), and the delete
 * affordance is a real `<button>` so the row stays operable without the gesture.
 */
function NotificationRow({
  notification,
  onOpen,
  onDelete,
}: {
  notification: Notification;
  onOpen: () => void;
  onDelete: () => void;
}): JSX.Element {
  const unread = !notification.is_read;
  const prefersReducedMotion = useReducedMotion();

  const containerRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const movedRef = useRef(false);
  const armedRef = useRef(false);
  const [removing, setRemoving] = useState(false);

  // Fade/scale the trash affordance in as the card slides left.
  const affordanceOpacity = useTransform(x, [-72, -20, 0], [1, 0.5, 0]);
  const affordanceScale = useTransform(x, [-96, -40, 0], [1, 0.7, 0.55]);

  const cardWidth = (): number => containerRef.current?.offsetWidth ?? 320;

  const springBack = (): void => {
    if (prefersReducedMotion) {
      x.set(0);
      return;
    }
    void animate(x, 0, { type: 'spring', stiffness: 550, damping: 45 });
  };

  const commitDelete = (): void => {
    if (removing) return;
    setRemoving(true);
    if (prefersReducedMotion) {
      onDelete();
      return;
    }
    // Slide the card off-screen, then remove it (the optimistic cache update
    // unmounts this row right after).
    void animate(x, -cardWidth(), {
      duration: 0.2,
      ease: [0.4, 0, 1, 1],
      onComplete: onDelete,
    });
  };

  const bind = useDrag(
    ({ first, last, movement: [mx], velocity: [vx], direction: [dx], tap }) => {
      if (removing) return;
      if (first) {
        movedRef.current = false;
        armedRef.current = false;
      }
      // A tap is handled by the native onClick below — don't treat it as a swipe.
      if (tap) return;
      movedRef.current = true;

      const clamped = Math.min(0, mx); // clamp rightward drag to rest
      const threshold = cardWidth() * 0.4;

      if (!last) {
        x.set(clamped);
        // Light haptic when crossing the delete threshold during the drag.
        if (!armedRef.current && -clamped >= threshold) {
          armedRef.current = true;
          haptics.tap();
        } else if (armedRef.current && -clamped < threshold) {
          armedRef.current = false;
        }
        return;
      }

      const flicked = vx > 0.5 && dx < 0;
      if (-clamped >= threshold || flicked) {
        commitDelete();
      } else {
        springBack();
      }
    },
    { axis: 'x', filterTaps: true, pointer: { touch: true } },
  );

  const handleClick = (): void => {
    // Suppress the click synthesised at the end of a swipe.
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    onOpen();
  };

  return (
    <div ref={containerRef} className="relative overflow-hidden">
      {/* Delete affordance revealed from the right edge as the card slides left. */}
      <button
        type="button"
        tabIndex={removing ? -1 : 0}
        aria-label={`Delete notification: ${notification.title}`}
        onClick={commitDelete}
        className="absolute inset-0 flex items-center justify-end bg-danger pl-4 pr-5 text-white"
      >
        <motion.span
          style={{ opacity: affordanceOpacity, scale: affordanceScale }}
          className="flex flex-col items-center gap-0.5"
        >
          <Trash2 className="h-5 w-5" strokeWidth={1.75} />
          <span className="text-caption font-medium leading-none">Delete</span>
        </motion.span>
      </button>

      <motion.div style={{ x }} className="relative">
        <button
          type="button"
          {...(bind() as React.HTMLAttributes<HTMLButtonElement>)}
          onClick={handleClick}
          className="group flex w-full touch-pan-y items-start gap-3 bg-card px-4 py-3.5 text-left transition-colors duration-fast hover:bg-sunken/60 active:bg-sunken"
        >
          <span
            className={cn(
              'mt-1.5 block h-2 w-2 shrink-0 rounded-full',
              unread ? 'bg-primary-600' : 'bg-hairline-strong/60',
            )}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <MarqueeText as="p" className={cn('text-body text-ink', unread && 'font-semibold')}>
                {notification.title}
              </MarqueeText>
              <span className="shrink-0 text-caption text-ink-muted">
                {formatRelative(notification.created_at)}
              </span>
            </div>
            <p className="mt-0.5 line-clamp-2 text-body-sm leading-snug text-ink-secondary">
              {toMessageSnippet(notification.message)}
            </p>
          </div>
          <ChevronRight
            className="mt-0.5 h-4 w-4 shrink-0 self-center text-ink-muted"
            strokeWidth={1.5}
          />
        </button>
      </motion.div>
    </div>
  );
}

function NotificationDetail({
  notification,
  onOpenTarget,
}: {
  notification: Notification;
  onOpenTarget: () => void;
}): JSX.Element {
  const target = getNotificationTarget(notification);
  const html = toRenderableMessageHtml(notification.message);
  // A broadcast opened from the bell must offer its files just as it does in the
  // Communication Centre — the same authorised, per-request download either way.
  const attachments = readAttachments(notification.metadata);

  return (
    <div className="space-y-5 pb-4">
      <Card noGrain className="rounded-2xl bg-sunken/80 px-4 py-4 shadow-none">
        <MessageBody html={html} />
      </Card>

      <AttachmentList
        tenantId={notification.tenant_id ?? null}
        notificationId={notification.notification_id}
        attachments={attachments}
      />

      {target ? (
        <Button type="button" variant="primary" size="md" className="w-full" onClick={onOpenTarget}>
          {target.kind === 'external' ? 'Open link' : 'Open related page'}
          <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
        </Button>
      ) : null}
    </div>
  );
}
